import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const AdminBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const response = await axios.get('/api/bookings/admin/all', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBookings(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching bookings:', error);
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this booking?')) {
      try {
        await axios.delete(`/api/bookings/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Booking deleted');
        fetchBookings();
      } catch (error) {
        alert('Error deleting booking');
      }
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: { background: '#fff3cd', color: '#856404', text: 'Pending Payment' },
      confirmed: { background: '#e8f5e9', color: '#2e7d32', text: 'Confirmed' },
      rejected: { background: '#ffebee', color: '#c62828', text: 'Rejected' },
      cancelled: { background: '#f5f5f5', color: '#757575', text: 'Cancelled' },
      completed: { background: '#e3f2fd', color: '#1565c0', text: 'Completed' }
    };
    const style = styles[status] || styles.pending;
    return (
      <span style={{
        background: style.background,
        color: style.color,
        padding: '0.35rem 0.85rem',
        borderRadius: '12px',
        fontSize: '0.85rem',
        fontWeight: 600
      }}>
        {style.text}
      </span>
    );
  };

  const filteredBookings = bookings.filter(booking => {
    if (filter === 'all') return true;
    return booking.status === filter;
  });

  if (loading) return <div style={styles.loading}>Loading...</div>;

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>All Bookings</h1>
      <p style={styles.subtitle}>View and manage all customer bookings</p>

      {/* Filter Tabs */}
      <div style={styles.filterTabs}>
        <button 
          onClick={() => setFilter('all')}
          style={{...styles.tab, ...(filter === 'all' ? styles.activeTab : {})}}
        >
          All ({bookings.length})
        </button>
        <button 
          onClick={() => setFilter('pending')}
          style={{...styles.tab, ...(filter === 'pending' ? styles.activeTab : {})}}
        >
          Pending ({bookings.filter(b => b.status === 'pending').length})
        </button>
        <button 
          onClick={() => setFilter('confirmed')}
          style={{...styles.tab, ...(filter === 'confirmed' ? styles.activeTab : {})}}
        >
          Confirmed ({bookings.filter(b => b.status === 'confirmed').length})
        </button>
        <button 
          onClick={() => setFilter('rejected')}
          style={{...styles.tab, ...(filter === 'rejected' ? styles.activeTab : {})}}
        >
          Rejected ({bookings.filter(b => b.status === 'rejected').length})
        </button>
      </div>

      {/* Bookings Table */}
      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead>
            <tr style={styles.tableHeader}>
              <th style={styles.th}>User</th>
              <th style={styles.th}>Phone</th>
              <th style={styles.th}>M-Pesa Name</th>
              <th style={styles.th}>Activity</th>
              <th style={styles.th}>Day & Time</th>
              <th style={styles.th}>Amount</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Booked On</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredBookings.length === 0 ? (
              <tr>
                <td colSpan="9" style={styles.emptyState}>
                  No bookings found
                </td>
              </tr>
            ) : (
              filteredBookings.map(booking => (
                <tr key={booking._id} style={styles.tableRow}>
                  <td style={styles.td}>
                    <strong>{booking.userName}</strong>
                  </td>
                  <td style={styles.td}>{booking.userPhone}</td>
                  <td style={styles.td}>
                    <span style={styles.mpesaName}>{booking.mpesaName}</span>
                  </td>
                  <td style={styles.td}>
                    {booking.activityId?.name || 'N/A'}
                  </td>
                  <td style={styles.td}>
                    {booking.activityId?.day}<br/>
                    <small style={{color: '#888'}}>{booking.activityId?.time}</small>
                  </td>
                  <td style={styles.td}>
                    <strong style={{color: '#1a5632'}}>KES {booking.paymentAmount}</strong>
                  </td>
                  <td style={styles.td}>
                    {getStatusBadge(booking.status)}
                  </td>
                  <td style={styles.td}>
                    {new Date(booking.createdAt).toLocaleDateString()}<br/>
                    <small style={{color: '#888'}}>
                      {new Date(booking.createdAt).toLocaleTimeString()}
                    </small>
                  </td>
                  <td style={styles.td}>
                    <button 
                      onClick={() => handleDelete(booking._id)}
                      style={styles.btnDelete}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const styles = {
  container: { maxWidth: '1800px', margin: '0 auto', padding: '2rem', background: '#fafafa', minHeight: '100vh' },
  loading: { textAlign: 'center', padding: '4rem', fontSize: '1.2rem' },
  title: { fontSize: '2.5rem', marginBottom: '0.5rem', color: '#1a5632', fontFamily: 'Playfair Display, serif' },
  subtitle: { color: '#5A5A5A', marginBottom: '2rem', fontSize: '1.1rem' },
  
  filterTabs: { display: 'flex', gap: '1rem', marginBottom: '2rem', flexWrap: 'wrap' },
  tab: { 
    padding: '0.65rem 1.25rem', 
    background: 'white', 
    border: '1px solid #e0e0e0', 
    borderRadius: '8px', 
    cursor: 'pointer', 
    fontSize: '0.9rem', 
    fontWeight: 500, 
    color: '#5A5A5A', 
    transition: 'all 0.2s' 
  },
  activeTab: { 
    background: '#1a5632', 
    color: 'white', 
    borderColor: '#1a5632' 
  },
  
  tableContainer: { background: 'white', borderRadius: '15px', overflow: 'hidden', boxShadow: '0 2px 15px rgba(0,0,0,0.08)' },
  table: { width: '100%', borderCollapse: 'collapse' },
  tableHeader: { background: '#fafafa', borderBottom: '2px solid #e0e0e0' },
  th: { padding: '1rem', textAlign: 'left', fontWeight: 600, fontSize: '0.9rem', color: '#2C2C2C' },
  tableRow: { borderBottom: '1px solid #f0f0f0', transition: 'background 0.2s' },
  td: { padding: '1rem', fontSize: '0.9rem', color: '#2C2C2C' },
  emptyState: { textAlign: 'center', padding: '3rem', color: '#888', fontSize: '1.1rem' },
  mpesaName: { background: '#e8f5e9', padding: '0.25rem 0.75rem', borderRadius: '12px', fontWeight: 600, color: '#1a5632', fontSize: '0.85rem' },
  btnDelete: { 
    background: 'white', 
    color: '#d32f2f', 
    padding: '0.5rem 1rem', 
    border: '1px solid #ffcdd2', 
    borderRadius: '6px', 
    cursor: 'pointer', 
    fontSize: '0.85rem',
    fontWeight: 500,
    transition: 'all 0.2s'
  }
};

export default AdminBookings;